import type React from "react";
import { fmtHours, fmtInt } from "@/lib/format";
import type { Slide, SlideContext } from "../slide.types";
import { Tile } from "../visuals/Tile";
import { TopBars, type TopBarItem } from "../visuals/TopBars";
import { ACCENT_BLUE, ACCENT_ORANGE } from "../colors";

function TopUsersSlideView({ data, resetKey }: SlideContext): React.JSX.Element {
  const users = data.methodology.users;
  if (users.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-white/40 text-sm font-body">
          No user data available for this period.
        </p>
      </div>
    );
  }

  // `users` is already the backend's top-10-by-session-count list (see
  // methodology.ts's finalizeUserMetrics) — re-sort by productive_hours here
  // so the "Top 5 — Productive Hours" leaderboard's order actually matches
  // what it's labeled as, rather than inheriting the session-count order.
  const top5 = [...users].sort((a, b) => b.productive_hours - a.productive_hours).slice(0, 5);
  const totalSessions = users.reduce((s, u) => s + u.sessions, 0);
  const topHours = users.reduce((s, u) => s + u.productive_hours, 0);

  const bars: TopBarItem[] = top5.map((u) => ({
    label: u.store ? `${u.user} · ${u.store}` : u.user,
    value: u.productive_hours,
    display: fmtHours(u.productive_hours),
  }));

  return (
    <>
      <div className="grid grid-cols-3 gap-4 mb-4">
        <Tile
          label="Active Users"
          value={data.methodology.kpis.total_active_users}
          format={(v) => fmtInt(Math.round(v))}
          delay={100}
          resetKey={resetKey}
        />
        <Tile
          label="Sessions (Top 10)"
          value={totalSessions}
          format={(v) => fmtInt(Math.round(v))}
          delay={200}
          resetKey={resetKey}
        />
        <Tile
          label="Productive Hours (Top 10)"
          value={topHours}
          format={fmtHours}
          delay={300}
          accent={ACCENT_ORANGE}
          resetKey={resetKey}
        />
      </div>
      <TopBars
        title="Top 5 Users — Productive Hours"
        items={bars}
        delay={400}
        accentTop={ACCENT_ORANGE}
        accentRest={ACCENT_BLUE}
      />
    </>
  );
}

export const topUsersSlide: Slide = {
  id: "top-users",
  title: "Top Users",
  render: (ctx) => <TopUsersSlideView {...ctx} />,
};
